// src/components/CustomerOrderPage/CustomerOrderPage.jsx
// Restaurant-style menu with event branding, course templates, and tag filtering

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import api, {
	getGuestHeaders,
	getGuestSessionToken,
	setGuestSessionToken,
	clearGuestSessionToken,
	getGuestDisplayName,
	setGuestDisplayName,
} from '../../utils/api';
import LoadingOverlay from '../Shared/LoadingOverlay';
import BackButton from '../Shared/BackButton';
import ThankYouPopup from './ThankYouPopup';
import PoweredByFooter from './PoweredByFooter';
import MealRatingPrompt from './MealRatingPrompt';
import { IconClock, IconChef, IconCheck, IconPlate, IconCart } from '../Shared/FigmaIcons';
import { prompt as askPrompt, confirm as askConfirm, toast } from '../Shared/feedback';
import { openPaystackCheckout } from '../../utils/paystackInline';

// Helpers shared with the meal card and cart so price formatting is
// consistent. All amounts in kobo on the wire; we display in naira.
const formatNaira = (kobo) => {
	const n = Number(kobo || 0) / 100;
	return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

// Replicates server effectiveMealKoboPrice for live cart totals + paid
// badging. Keep in sync with server/routes/customerRoutes.js.
const isMealPaid = (meal, mode) => {
	const behavior = meal?.paymentBehavior || 'inherit_event';
	if (behavior === 'paid') return true;
	if (behavior === 'included') return false;
	return mode === 'mixed' || mode === 'all_paid';
};
const mealKoboPrice = (meal, mode, variation) => {
	if (!meal) return 0;
	if (!isMealPaid(meal, mode)) return 0;
	const base = Number(meal.paidPriceKobo) || 0;
	const mod = Number(variation?.priceModifier) || 0;
	const modKobo = Math.abs(mod) > 10000 ? mod : mod * 100;
	return Math.max(0, base + modKobo);
};

// Multi-language menus (Tier 1, 2026-05-23). yo/ig/ha/pcm + English
// default. Locale stored in localStorage so a returning guest's choice
// persists across reloads + table changes.
const SUPPORTED_LOCALES = [
	{ code: 'en', label: 'English', short: 'EN' },
	{ code: 'yo', label: 'Yorùbá', short: 'YO' },
	{ code: 'ig', label: 'Igbo', short: 'IG' },
	{ code: 'ha', label: 'Hausa', short: 'HA' },
	{ code: 'pcm', label: 'Pidgin', short: 'PCM' },
];
const LOCALE_KEY = 'eventory.menuLocale';

// Pick the right meal name/description for the active locale. Sparse map
// fallback to the canonical English `meal.name`/`meal.description`.
const localizedMealField = (meal, field, locale) => {
	if (!meal) return '';
	const fallback = meal[field] || '';
	if (!locale || locale === 'en') return fallback;
	const mapField = field === 'name' ? 'nameByLocale' : 'descriptionByLocale';
	const map = meal[mapField];
	if (!map) return fallback;
	// Mongoose Maps on the wire serialise as plain objects most of the
	// time; but if the API ever sends a Map instance, support both.
	const value = typeof map.get === 'function' ? map.get(locale) : map[locale];
	return (value && String(value).trim()) || fallback;
};

// Lightweight signed-in detector — purely client-side and best-effort. We
// just want to show team members a small "you're signed in" banner when
// they scan a QR. No API call: if `localStorage.token` exists we treat
// them as signed in; the banner is informational only.
const useIsLoggedIn = () => {
	const [signedIn, setSignedIn] = useState(false);
	useEffect(() => {
		setSignedIn(!!localStorage.getItem('token'));
	}, []);
	return signedIn;
};

const CustomerOrderPage = () => {
	const [menuData, setMenuData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const [orderId, setOrderId] = useState(null);
	const [selectedItems, setSelectedItems] = useState([]);
	const [allergies, setAllergies] = useState([]);
	const [orderStatus, setOrderStatus] = useState('Draft');
	const [submitting, setSubmitting] = useState(false);
	const [actionLoading, setActionLoading] = useState(false);

	const [activeFilters, setActiveFilters] = useState([]);
	const [showCart, setShowCart] = useState(false);
	const [showThankYou, setShowThankYou] = useState(false);
	const [guestName, setGuestName] = useState(() => getGuestDisplayName());
	const [locale, setLocale] = useState(() => {
		if (typeof window === 'undefined') return 'en';
		const stored = window.localStorage.getItem(LOCALE_KEY);
		if (stored && SUPPORTED_LOCALES.some((l) => l.code === stored)) return stored;
		// Best-effort guess from browser language; default to English.
		const nav = window.navigator?.language || '';
		const short = nav.toLowerCase().split('-')[0];
		const match = SUPPORTED_LOCALES.find((l) => l.code === short);
		return match ? match.code : 'en';
	});
	const updateLocale = (next) => {
		setLocale(next);
		try { window.localStorage.setItem(LOCALE_KEY, next); } catch (e) { /* ignore */ }
	};
	const isLoggedIn = useIsLoggedIn();

	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const hashParams = new URLSearchParams(location.hash.startsWith('#') ? location.hash.slice(1) : location.hash);
	const tableId = params.get('tableId');
	const accessCode = params.get('accessCode');
	const qrToken =
		hashParams.get('qrToken') ||
		hashParams.get('token') ||
		params.get('qrToken') ||
		params.get('token');
	const guestNameFromUrl = params.get('guestName');
	// Event guest code (the no-QR path via /order) — kept in the URL so the
	// menu request authenticates and "Change table" returns to this event.
	const eventCodeParam = (params.get('ecode') || '').toUpperCase();

	const branding = menuData?.event?.branding || {
		primaryColor: '#3D1E6D',
		accentColor: '#FB8601',
		welcomeMessage: 'Welcome! Please select your meal.',
	};

	const clearQrTokenFromUrl = useCallback(() => {
		const nextParams = new URLSearchParams(location.search);
		const nextHashParams = new URLSearchParams(
			location.hash.startsWith('#') ? location.hash.slice(1) : location.hash
		);
		let changed = false;

		['token', 'qrToken'].forEach((key) => {
			if (nextParams.has(key)) {
				nextParams.delete(key);
				changed = true;
			}
			if (nextHashParams.has(key)) {
				nextHashParams.delete(key);
				changed = true;
			}
		});

		if (!changed || typeof window === 'undefined') return;

		const nextSearch = nextParams.toString();
		const nextHash = nextHashParams.toString();
		const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${
			nextHash ? `#${nextHash}` : ''
		}`;
		window.history.replaceState({}, '', nextUrl);
	}, [location.hash, location.pathname, location.search]);

	useEffect(() => {
		if (!tableId) return;

		const fetchMenu = async () => {
			setLoading(true);
			setError(null);
			try {
				const resolvedGuestName = guestNameFromUrl || guestName;
				// Re-scanning the table QR used to mint a NEW guest session every
				// time, which orphaned the previous order (the old session owned
				// it) and quietly created a duplicate. If this device already has
				// a session for THIS table, keep it — the existing order resumes.
				const reusableSession =
					Boolean(getGuestSessionToken()) && localStorage.getItem('orderTableId') === tableId;
				if (qrToken && !reusableSession) {
					try {
						const sessionRes = await api.post('/customer/session/start', {
							tableId,
							qrToken,
							...(resolvedGuestName ? { guestName: resolvedGuestName } : {}),
						});
						if (sessionRes.data?.guestToken) {
							setGuestSessionToken(sessionRes.data.guestToken);
						}
						clearQrTokenFromUrl();
					} catch (sessionErr) {
						// Best-effort: a public event still resolves the menu below
						// without it, so don't block the menu on a cold-start blip.
						console.warn('session/start failed, continuing to menu:', sessionErr?.message);
					}
				}
				const requestParams = {};
				// Keep the QR token as fallback auth: if the reused session turns
				// out stale/expired, the menu route re-validates via the QR and
				// issues a fresh session instead of 401-ing the guest.
				if (qrToken) requestParams.token = qrToken;
				if (eventCodeParam) requestParams.ecode = eventCodeParam;
				if (accessCode) requestParams.accessCode = accessCode;
				if (resolvedGuestName) requestParams.guestName = resolvedGuestName;
				// Retry transient failures (sleeping Eco dyno cold-start) so the
				// menu loads without the guest having to refresh.
				let res;
				for (let attempt = 1; ; attempt += 1) {
					try {
						res = await api.get(`/customer/menu/${tableId}`, {
							params: requestParams,
							headers: getGuestHeaders(),
						});
						break;
					} catch (menuErr) {
						const status = menuErr.response?.status;
						if ((!status || status >= 500) && attempt < 3) {
							await new Promise((r) => setTimeout(r, 1500 * attempt));
							continue;
						}
						throw menuErr;
					}
				}
				if (res.data?.guestToken) {
					setGuestSessionToken(res.data.guestToken);
				}
				if (resolvedGuestName) {
					setGuestDisplayName(resolvedGuestName);
				}
				setMenuData(res.data);
			} catch (err) {
				console.error('Error fetching menu:', err);
				if (err.response?.status === 401 && getGuestSessionToken()) {
					clearGuestSessionToken();
					setError('Your guest session expired. Please reopen your QR code or access link.');
				} else {
					setError(err.response?.data?.msg || 'Failed to load menu. Please try again.');
				}
			} finally {
				setLoading(false);
			}
		};

		fetchMenu();
	}, [tableId, accessCode, qrToken, eventCodeParam, guestNameFromUrl, guestName, clearQrTokenFromUrl]);

	useEffect(() => {
		if (menuData?.event?.name) {
			document.title = `${menuData.event.name} | Eventory`;
		}
		return () => {
			document.title = 'Eventory';
		};
	}, [menuData?.event?.name]);

	const createNewOrder = useCallback(async () => {
		if (!tableId) return;
		try {
			const res = await api.post(
				'/customer/orders',
				{
					items: [],
					allergies: [],
				},
				{ headers: getGuestHeaders() }
			);
			// Offline cold-start: a queued POST resolves with a 202 {queued}
			// pseudo-response that carries no server id. We can't build a cart
			// against a missing id (Phase 1 can't surface a replayed POST's id),
			// so keep orderId null and tell the guest plainly instead of storing
			// a junk "undefined". The normal path — a draft created while the
			// tablet was online — is unaffected.
			if (res.data?.queued || !res.data?._id) {
				setOrderId(null);
				setError('You’re offline. Reconnect to start your order. The menu stays viewable.');
				return;
			}
			setOrderId(res.data._id);
			localStorage.setItem('currentOrderId', res.data._id);
			localStorage.setItem('orderTableId', tableId);
			setSelectedItems([]);
			setAllergies(res.data.allergies || []);
			setOrderStatus(res.data.status || 'Draft');
		} catch (err) {
			console.error('Error creating order:', err);
			setError('Failed to create order. Please try again.');
		}
	}, [tableId]);

	const fetchExistingOrder = useCallback(async (existingOrderId) => {
		try {
			const res = await api.get(`/customer/orders/${existingOrderId}`, {
				headers: getGuestHeaders(),
			});
			const order = res.data;
			const orderTableId = order.table?._id || order.table;
			if (orderTableId !== tableId) {
				localStorage.removeItem('currentOrderId');
				localStorage.removeItem('orderTableId');
				await createNewOrder();
				return;
			}

			setOrderId(order._id);
			setSelectedItems(order.items.map((i) => ({
				...i.menuItem,
				quantity: i.quantity,
				customization: i.customization,
				variation: i.selectedVariation || undefined,
			})));
			setAllergies(order.allergies || []);
			setOrderStatus(order.status);
		} catch (err) {
			console.error('Error fetching existing order:', err);
			localStorage.removeItem('currentOrderId');
			localStorage.removeItem('orderTableId');
			await createNewOrder();
		}
	}, [createNewOrder, tableId]);

	useEffect(() => {
		if (!tableId || !menuData) return;

		let cancelled = false;

		const initOrder = async () => {
			setActionLoading(true);
			try {
				const storedOrderId = localStorage.getItem('currentOrderId');
				const storedTableId = localStorage.getItem('orderTableId');
				if (storedOrderId && storedTableId !== tableId) {
					localStorage.removeItem('currentOrderId');
					localStorage.removeItem('orderTableId');
					await createNewOrder();
					return;
				}
				if (storedOrderId) {
					await fetchExistingOrder(storedOrderId);
				} else {
					await createNewOrder();
				}
			} finally {
				if (!cancelled) {
					setActionLoading(false);
				}
			}
		};

		initOrder();
		return () => {
			cancelled = true;
		};
	}, [createNewOrder, fetchExistingOrder, menuData, tableId]);

	useEffect(() => {
		if (!orderId || orderStatus === 'Draft') return;
		const interval = setInterval(async () => {
			// Skip while offline — the connectivity layer handles reconnection,
			// so a dropped status poll is expected, not an error worth logging.
			if (typeof navigator !== 'undefined' && !navigator.onLine) return;
			try {
				const res = await api.get(`/customer/orders/${orderId}`, {
					headers: getGuestHeaders(),
				});
				setOrderStatus(res.data.status);
			} catch (err) {
				// Only surface real server errors; network blips are normal offline.
				if (err?.response) console.error('Error refreshing status:', err);
			}
		}, 10000);
		return () => clearInterval(interval);
	}, [orderId, orderStatus]);

	const itemsByCategory = useMemo(() => {
		const counts = {};
		selectedItems.forEach((item) => {
			const catId = item.courseCategory?._id || item.courseCategory || 'uncategorized';
			counts[catId] = (counts[catId] || 0) + (item.quantity || 1);
		});
		return counts;
	}, [selectedItems]);

	const isCategoryLimitReached = useCallback((categoryId) => {
		if (!menuData?.courseTemplate) return false;
		const catConfig = menuData.courseTemplate.categories.find((c) => c.category?._id === categoryId);
		if (!catConfig || catConfig.limit === 0) return false;
		return (itemsByCategory[categoryId] || 0) >= catConfig.limit;
	}, [itemsByCategory, menuData]);

	const requiredCategoriesSatisfied = useMemo(() => {
		if (!menuData?.courseTemplate) return true;
		return menuData.courseTemplate.categories
			.filter((c) => c.required)
			.every((c) => {
				const count = itemsByCategory[c.category?._id] || 0;
				const minRequired = c.minRequired || 1;
				return count >= minRequired;
			});
	}, [itemsByCategory, menuData]);

	const filteredMeals = useMemo(() => {
		if (!menuData?.meals) return [];
		if (activeFilters.length === 0) return menuData.meals;
		return menuData.meals.filter((meal) => {
			if (!meal.tags || meal.tags.length === 0) return false;
			return activeFilters.every((filter) => meal.tags.includes(filter));
		});
	}, [activeFilters, menuData]);

	const mealsByCategory = useMemo(() => {
		const grouped = {};
		const categoryOrder = menuData?.courseTemplate?.categories || [];

		categoryOrder.forEach((catConfig) => {
			if (catConfig.category) {
				grouped[catConfig.category._id] = {
					category: catConfig.category,
					config: catConfig,
					meals: [],
				};
			}
		});

		grouped.uncategorized = {
			category: { _id: 'uncategorized', displayName: 'Other items', icon: '' },
			config: { limit: 0, required: false },
			meals: [],
		};

		filteredMeals.forEach((meal) => {
			const catId = meal.courseCategory?._id || 'uncategorized';
			if (grouped[catId]) {
				grouped[catId].meals.push(meal);
			} else {
				grouped.uncategorized.meals.push(meal);
			}
		});

		return Object.values(grouped).filter((g) => g.meals.length > 0);
	}, [filteredMeals, menuData]);

	// Cart lines are keyed by `mealId + variation name` so the same meal with
	// different variations (Chicken vs Beef) lives on separate lines — matching
	// the DoorDash / Uber Eats / Toast convention.
	const lineKey = (mealId, variationName) => `${mealId}::${variationName || ''}`;

	const persistItems = async (updatedItems) => {
		await api.put(
			`/customer/orders/${orderId}`,
			{
				items: updatedItems.map((i) => ({
					menuItemId: i._id,
					quantity: i.quantity,
					customization: i.customization || '',
					selectedVariation: i.variation || undefined,
				})),
			},
			{ headers: getGuestHeaders() }
		);
	};

	const handleAddItem = async (item, variation = null) => {
		const catId = item.courseCategory?._id || item.courseCategory;
		if (isCategoryLimitReached(catId)) {
			setError('Category limit reached');
			return;
		}

		if (!orderId) {
			setError('Preparing your order. Please try again in a moment.');
			return;
		}

		setActionLoading(true);
		try {
			const key = lineKey(item._id, variation?.name);
			const existingIndex = selectedItems.findIndex(
				(i) => lineKey(i._id, i.variation?.name) === key
			);
			let updatedItems;
			if (existingIndex >= 0) {
				updatedItems = selectedItems.map((i, idx) =>
					idx === existingIndex ? { ...i, quantity: (i.quantity || 1) + 1 } : i
				);
			} else {
				updatedItems = [...selectedItems, { ...item, quantity: 1, variation: variation || undefined }];
			}

			await persistItems(updatedItems);
			setSelectedItems(updatedItems);
		} catch (err) {
			console.error('Error adding item:', err);
			setError('Failed to add item.');
		} finally {
			setActionLoading(false);
		}
	};

	const handleRemoveItem = async (itemId, variationName = null) => {
		if (!orderId) {
			setError('Preparing your order. Please try again in a moment.');
			return;
		}
		setActionLoading(true);
		try {
			const key = lineKey(itemId, variationName);
			const existingItem = selectedItems.find(
				(i) => lineKey(i._id, i.variation?.name) === key
			);
			let updatedItems;
			if (existingItem && existingItem.quantity > 1) {
				updatedItems = selectedItems.map((i) =>
					lineKey(i._id, i.variation?.name) === key
						? { ...i, quantity: i.quantity - 1 }
						: i
				);
			} else {
				updatedItems = selectedItems.filter(
					(i) => lineKey(i._id, i.variation?.name) !== key
				);
			}

			await persistItems(updatedItems);
			setSelectedItems(updatedItems);
		} catch (err) {
			console.error('Error removing item:', err);
			setError('Failed to remove item.');
		} finally {
			setActionLoading(false);
		}
	};

	const guestPricingMode = menuData?.event?.guestPricing?.mode || 'all_included';
	const cartSubtotalKobo = selectedItems.reduce(
		(sum, line) => sum + mealKoboPrice(line, guestPricingMode, line.variation) * (line.quantity || 1),
		0
	);
	const cartHasPaidItems = cartSubtotalKobo > 0;

	const handleSubmitOrder = async () => {
		if (selectedItems.length === 0) {
			setError('Please select at least one item');
			return;
		}
		if (!requiredCategoriesSatisfied) {
			setError('Please complete all required selections');
			return;
		}

		if (!orderId) {
			setError('Preparing your order. Please try again in a moment.');
			return;
		}

		// All-included or no-paid-items cart: same submit flow as before.
		if (!cartHasPaidItems) {
			setSubmitting(true);
			try {
				const res = await api.put(
					`/customer/orders/${orderId}/status`,
					{ status: 'Pending' },
					{ headers: getGuestHeaders() }
				);
				setOrderStatus(res.data.status);
				setShowThankYou(true);
			} catch (err) {
				console.error('Error submitting order:', err);
				setError('Failed to submit order.');
			} finally {
				setSubmitting(false);
			}
			return;
		}

		// Paid cart: initialize Paystack, open inline checkout, poll for
		// webhook confirmation, then show the thank-you state. The server
		// promotes the order Draft→Pending in the webhook handler.
		setSubmitting(true);
		try {
			const initRes = await api.post(
				'/customer/payment/initialize',
				{ orderId },
				{ headers: getGuestHeaders() }
			);
			const { reference, access_code, publicKey, totalKobo, mock } = initRes.data || {};
			if (!reference) {
				throw new Error('Paystack did not return a reference.');
			}
			const checkout = await openPaystackCheckout({
				publicKey,
				email: `guest+${orderId}@eventory.app`,
				amountKobo: totalKobo || cartSubtotalKobo,
				reference,
				accessCode: access_code,
				mock: !!mock,
				metadata: { orderId },
			});
			if (checkout.status !== 'success') {
				toast.warn('Payment was cancelled. Your cart is still here, so try again whenever you’re ready.');
				return;
			}

			// In mock mode the Paystack popup is skipped → no webhook fires →
			// the order would never promote. Hit the dev mock-confirm endpoint
			// so the kitchen still sees the ticket during local testing.
			if (mock) {
				try {
					await api.post(
						'/customer/payment/mock-confirm',
						{ reference },
						{ headers: getGuestHeaders() }
					);
				} catch (mockErr) {
					console.warn('mock-confirm failed:', mockErr);
				}
			}

			// Poll status until the webhook flips it to success (usually < 5s).
			const maxAttempts = 20;
			let finalStatus = null;
			for (let i = 0; i < maxAttempts; i += 1) {
				// eslint-disable-next-line no-await-in-loop
				const statusRes = await api.get(`/customer/payment/status/${reference}`, {
					headers: getGuestHeaders(),
				});
				if (statusRes.data?.status === 'success') {
					finalStatus = statusRes.data;
					break;
				}
				if (statusRes.data?.status === 'failed') {
					throw new Error('Payment failed at the gateway.');
				}
				// eslint-disable-next-line no-await-in-loop
				await new Promise((r) => setTimeout(r, 1500));
			}
			if (!finalStatus) {
				toast.warn('Payment is taking longer than usual to confirm. It will go through, please wait.');
			}
			setOrderStatus('Pending');
			setShowThankYou(true);

			// Offer SMS receipt (optional).
			setTimeout(async () => {
				const phone = await askPrompt({
					title: 'Want a text receipt?',
					message: 'We can send your order confirmation by SMS. Skip if you don’t need one.',
					placeholder: '08012345678',
					confirmLabel: 'Send receipt',
					cancelLabel: 'No thanks',
				});
				if (phone && String(phone).trim()) {
					try {
						await api.post(
							'/customer/payment/sms-receipt',
							{ reference, phone: String(phone).trim() },
							{ headers: getGuestHeaders() }
						);
						toast.success('Receipt sent.');
					} catch (smsErr) {
						console.warn('SMS receipt failed:', smsErr);
					}
				}
			}, 800);
		} catch (err) {
			console.error('Payment error:', err);
			setError(err.response?.data?.msg || err.message || 'Payment failed. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	const toggleFilter = (tagName) => {
		setActiveFilters((prev) =>
			prev.includes(tagName) ? prev.filter((f) => f !== tagName) : [...prev, tagName]
		);
	};

	const handleNewOrder = () => {
		localStorage.removeItem('currentOrderId');
		localStorage.removeItem('orderTableId');
		setOrderId(null);
		setSelectedItems([]);
		setAllergies([]);
		setOrderStatus('Draft');
		setShowThankYou(false);
		createNewOrder();
	};

	// Guests can withdraw an order until the kitchen starts cooking it.
	const handleCancelOrder = async () => {
		const ok = await askConfirm({
			title: 'Cancel this order?',
			message: "It hasn't reached the cooks yet, so nothing is wasted. You can order again right after.",
			confirmLabel: 'Cancel order',
			tone: 'danger',
		});
		if (!ok) return;
		try {
			await api.put(
				`/customer/orders/${orderId}/status`,
				{ status: 'Cancelled' },
				{ headers: getGuestHeaders() }
			);
			toast.success('Order cancelled.');
			handleNewOrder();
		} catch (err) {
			toast.error(err.response?.data?.msg || 'Could not cancel. The kitchen may have started on it.');
		}
	};

	if (!tableId) {
		return <Navigate to="/order" replace />;
	}

	if (loading) {
		return <LoadingOverlay show={true} message="Loading menu..." fullScreen />;
	}

	if (orderStatus !== 'Draft') {
		return (
			<div
				className="ev-menu-page"
				style={{
					'--ev-brand-color': branding.primaryColor,
					'--ev-accent-color': branding.accentColor,
				}}
			>
				<OrderTrackingView
					orderStatus={orderStatus}
					orderId={orderId}
					selectedItems={selectedItems}
					allergies={allergies}
					eventName={menuData?.event?.name}
					onNewOrder={handleNewOrder}
					onCancelOrder={handleCancelOrder}
				/>
				<PoweredByFooter />
				{showThankYou && <ThankYouPopup onClose={() => setShowThankYou(false)} />}
			</div>
		);
	}

	const totalItems = selectedItems.reduce((sum, i) => sum + (i.quantity || 1), 0);
	const eventIdForAdmin = menuData?.event?._id;

	return (
		<div
			className="ev-menu-page"
			style={{
				'--ev-brand-color': branding.primaryColor,
				'--ev-accent-color': branding.accentColor,
			}}
		>
			{isLoggedIn && eventIdForAdmin && (
				<div className="ev-member-banner" role="status">
					<span>You're signed in as a team member. You can still order as a guest — or open the admin view.</span>
					<a href={`/admin/event/${eventIdForAdmin}/overview`} className="ev-member-banner-link">
						Open admin →
					</a>
				</div>
			)}
			<header className="ev-menu-header">
				<div className="ev-back-row ev-menu-back-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
					<BackButton to={eventCodeParam ? `/order/${eventCodeParam}` : '/order'} label="Change table" />
					<div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }} aria-label="Menu language">
						{SUPPORTED_LOCALES.map((l) => (
							<button
								key={l.code}
								type="button"
								onClick={() => updateLocale(l.code)}
								aria-pressed={locale === l.code}
								title={l.label}
								style={{
									padding: '4px 8px',
									fontSize: 11,
									fontWeight: 700,
									letterSpacing: '0.05em',
									border: '1px solid ' + (locale === l.code ? 'var(--ev-brand, #3D1E6D)' : 'rgba(17,17,19,0.12)'),
									borderRadius: 999,
									background: locale === l.code ? 'var(--ev-brand, #3D1E6D)' : 'white',
									color: locale === l.code ? 'white' : '#111113',
									cursor: 'pointer',
								}}
							>
								{l.short}
							</button>
						))}
					</div>
				</div>
				{branding.logo && <img src={branding.logo} alt="Event Logo" className="ev-menu-logo" />}
				<h1 className="ev-menu-event">{menuData?.event?.name || 'Menu'}</h1>
				<p className="ev-menu-welcome">{branding.welcomeMessage}</p>
				<button
					type="button"
					className="ev-menu-name-chip"
					onClick={async () => {
						const next = await askPrompt({
							title: 'Who are we calling for your order?',
							message: 'Adds your name to the kitchen ticket so a server can find you faster.',
							defaultValue: guestName || '',
							placeholder: 'Your first name',
							confirmLabel: 'Save name',
						});
						if (next === null) return;
						const clean = String(next || '').trim().slice(0, 60);
						setGuestName(clean);
						setGuestDisplayName(clean);
						// Refresh the menu so the server updates the Guest record with the new name.
						if (tableId) {
							try {
								const requestParams = {};
								if (qrToken) requestParams.token = qrToken;
								if (eventCodeParam) requestParams.ecode = eventCodeParam;
								if (accessCode) requestParams.accessCode = accessCode;
								if (clean) requestParams.guestName = clean;
								await api.get(`/customer/menu/${tableId}`, {
									params: requestParams,
									headers: getGuestHeaders(),
								});
								toast.success(clean ? `Got it — orders go out as ${clean}` : 'Name cleared');
							} catch (err) {
								toast.error('Could not save your name. Try again in a moment.');
							}
						}
					}}
					aria-label="Set your name"
				>
					<span className="ev-menu-name-chip-label">Ordering as</span>
					<span className="ev-menu-name-chip-value">
						{(guestName || 'Tap to add your name')}
						{menuData?.table?.tableNumber ? ` · Table ${menuData.table.tableNumber}` : ''}
					</span>
				</button>
			</header>

			{menuData?.availableTags?.length > 0 && (
				<div className="ev-menu-filter-row">
					<button
						onClick={() => setActiveFilters([])}
						className={`ev-menu-filter-btn ${activeFilters.length === 0 ? 'is-active' : ''}`}
					>
						All
					</button>
					{menuData.availableTags.map((tag) => {
						const isActive = activeFilters.includes(tag.name);
						return (
							<button
								key={tag._id}
								onClick={() => toggleFilter(tag.name)}
								className={`ev-menu-filter-btn ${isActive ? 'is-active' : ''}`}
								style={isActive && tag.color ? {
									borderColor: tag.color,
									color: tag.color,
									backgroundColor: `${tag.color}1f`,
								} : undefined}
							>
								{tag.name}
							</button>
						);
					})}
				</div>
			)}

			<main className="ev-menu-main">
				{error && (
					<div className="ev-alert ev-alert-danger">
						{error}
						<button className="ev-icon-btn" onClick={() => setError(null)} aria-label="Close error">
							×
						</button>
					</div>
				)}

				{mealsByCategory.map(({ category, config, meals }) => (
					<MenuCategory
						key={category._id}
						category={category}
						config={config}
						meals={meals}
						selectedItems={selectedItems}
						itemCount={itemsByCategory[category._id] || 0}
						onAddItem={handleAddItem}
						onRemoveItem={handleRemoveItem}
						isLimitReached={isCategoryLimitReached(category._id)}
						loading={actionLoading || !orderId}
						guestPricingMode={guestPricingMode}
						locale={locale}
					/>
				))}

				{filteredMeals.length === 0 && (
					<div className="ev-menu-empty">
						<p>No items match your filters.</p>
						<button className="ev-soft-btn ev-top-gap" onClick={() => setActiveFilters([])}>
							Clear filters
						</button>
					</div>
				)}
			</main>

			{totalItems > 0 && (
				<div className="ev-menu-cart-float">
					<button className="ev-menu-cart-toggle" onClick={() => setShowCart(!showCart)}>
						<span className="ev-menu-cart-toggle-main">
							<IconCart className="ev-icon-svg" />
							<span>View order · {totalItems} item{totalItems !== 1 ? 's' : ''}</span>
						</span>
						<span className={`ev-menu-cart-state ${requiredCategoriesSatisfied ? 'is-ready' : 'is-incomplete'}`}>
							{requiredCategoriesSatisfied ? 'Ready to submit' : 'Add more to complete'}
						</span>
					</button>
				</div>
			)}

			{showCart && (
				<CartBottomSheet
					selectedItems={selectedItems}
					menuData={menuData}
					itemsByCategory={itemsByCategory}
					requiredCategoriesSatisfied={requiredCategoriesSatisfied}
					onRemoveItem={handleRemoveItem}
					onSubmit={handleSubmitOrder}
					onClose={() => setShowCart(false)}
					submitting={submitting}
					guestPricingMode={guestPricingMode}
					subtotalKobo={cartSubtotalKobo}
					hasPaidItems={cartHasPaidItems}
				/>
			)}

			<PoweredByFooter />

			{showThankYou && <ThankYouPopup onClose={() => setShowThankYou(false)} />}
		</div>
	);
};

const MenuCategory = ({
	category,
	config,
	meals,
	selectedItems,
	itemCount,
	onAddItem,
	onRemoveItem,
	isLimitReached,
	loading,
	guestPricingMode = 'all_included',
	locale = 'en',
}) => {
	const limit = config?.limit || 0;
	const isRequired = config?.required;

	return (
		<section className="ev-menu-category">
			<div className="ev-menu-category-head">
				<h2 className="ev-menu-category-title">
					<span>{category.icon}</span>
					{category.displayName}
					{isRequired && <span className="is-required">*</span>}
				</h2>
				{limit > 0 && (
					<span className={`ev-menu-limit ${itemCount >= limit ? 'is-full' : ''}`}>
						{itemCount}/{limit}
					</span>
				)}
			</div>

			<div className="ev-menu-grid">
				{meals.map((meal) => {
					// All cart lines for this meal (may be multiple if guest picked
					// different variations on different visits).
					const linesForMeal = selectedItems.filter((i) => i._id === meal._id);
					return (
						<MealCard
							key={meal._id}
							meal={meal}
							linesForMeal={linesForMeal}
							onAdd={(variation) => onAddItem(meal, variation)}
							onRemove={(variationName) => onRemoveItem(meal._id, variationName)}
							isLimitReached={isLimitReached}
							loading={loading}
							guestPricingMode={guestPricingMode}
							locale={locale}
						/>
					);
				})}
			</div>
		</section>
	);
};

const MealCard = ({ meal, linesForMeal = [], onAdd, onRemove, isLimitReached, loading, guestPricingMode = 'all_included', locale = 'en' }) => {
	const displayName = localizedMealField(meal, 'name', locale);
	const displayDescription = localizedMealField(meal, 'description', locale);
	const isOutOfStock = meal.availableQuantity <= 0;
	const allVariations = meal.variations || [];
	const availableVariations = allVariations.filter((v) => v.isAvailable !== false);
	const hasVariations = allVariations.length > 0;
	const isPaidMeal = isMealPaid(meal, guestPricingMode);

	// Pre-select the first *available* variation so the CTA is never blocked.
	// Re-defaults if the user's choice goes out of stock mid-session.
	const [selectedVariationName, setSelectedVariationName] = useState(
		() => availableVariations[0]?.name || null
	);
	useEffect(() => {
		if (!hasVariations) return;
		const stillAvailable = availableVariations.some((v) => v.name === selectedVariationName);
		if (!stillAvailable) {
			setSelectedVariationName(availableVariations[0]?.name || null);
		}
	}, [hasVariations, availableVariations, selectedVariationName]);

	// Quantity displayed is for the *currently selected variation's* line.
	const currentLine = hasVariations
		? linesForMeal.find((l) => l.variation?.name === selectedVariationName)
		: linesForMeal[0];
	const quantity = currentLine?.quantity || 0;

	// "Total across all variations of this meal" — small hint when guest has
	// other variations of the same item in the cart.
	const totalAcrossVariations = linesForMeal.reduce((sum, l) => sum + (l.quantity || 1), 0);
	const otherVariationCount = totalAcrossVariations - quantity;

	const selectedVariation = hasVariations
		? availableVariations.find((v) => v.name === selectedVariationName) || null
		: null;
	const isDisabled = isLimitReached || isOutOfStock;

	const initials = (meal.name || '?')
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word[0])
		.join('')
		.toUpperCase();

	const handleAdd = () => onAdd(selectedVariation);
	const handleRemove = () => onRemove(selectedVariation?.name);

	const addLabel = hasVariations && selectedVariation
		? `Add ${selectedVariation.name}`
		: 'Add to Order';

	return (
		<div className={`ev-meal-card ${quantity > 0 ? 'is-selected' : ''} ${isDisabled && quantity === 0 ? 'is-dimmed' : ''}`}>
			<div className="ev-meal-visual">
				{meal.imageUrl ? (
					<img src={meal.imageUrl} alt={displayName} />
				) : (
					<div className="ev-meal-fallback" aria-hidden="true">
						<span className="ev-meal-fallback-monogram">{initials}</span>
					</div>
				)}
			</div>

			<div className="ev-meal-body">
				<div className="ev-meal-row">
					<h3 className="ev-meal-name">{displayName}</h3>
					{quantity > 0 && <span className="ev-meal-count">x{quantity}</span>}
				</div>

				{isPaidMeal && (
					<div className="ev-meal-price-row">
						<span className="ev-meal-price">{formatNaira(meal.paidPriceKobo)}</span>
						<span className="ev-meal-price-badge">Premium</span>
					</div>
				)}

				{displayDescription && <p className="ev-meal-desc">{displayDescription}</p>}

				{hasVariations && (
					<div
						className="ev-meal-variations"
						role="radiogroup"
						aria-label={`Options for ${displayName}`}
					>
						{allVariations.map((v, idx) => {
							const isAvail = v.isAvailable !== false;
							const isActive = isAvail && v.name === selectedVariationName;
							return (
								<button
									key={`${v.name}-${idx}`}
									type="button"
									role="radio"
									aria-checked={isActive}
									className={`ev-meal-variation-chip${isActive ? ' is-active' : ''}${!isAvail ? ' is-soldout' : ''}`}
									disabled={!isAvail}
									onClick={() => setSelectedVariationName(v.name)}
								>
									<span>{v.name}</span>
									{!isAvail && <span className="ev-meal-variation-tag">Sold out</span>}
								</button>
							);
						})}
					</div>
				)}

				{meal.tags?.length > 0 && (
					<div className="ev-meal-tags">
						{meal.tags.slice(0, 3).map((tag) => (
							<span key={tag} className="ev-meal-tag">{tag}</span>
						))}
					</div>
				)}

				{otherVariationCount > 0 && (
					<p className="ev-meal-variation-note">
						{otherVariationCount} other option{otherVariationCount === 1 ? '' : 's'} also in your cart
					</p>
				)}

				{isOutOfStock ? (
					<div className="ev-meal-unavailable">Out of Stock</div>
				) : quantity > 0 ? (
					<div className="ev-meal-qty-row">
						<button className="ev-meal-qty-btn" onClick={handleRemove} disabled={loading}>−</button>
						<span className="ev-meal-qty">{quantity}</span>
						<button
							className="ev-meal-qty-btn is-primary"
							onClick={handleAdd}
							disabled={loading || isLimitReached}
						>
							+
						</button>
					</div>
				) : (
					<button
						className="ev-meal-add-btn"
						onClick={handleAdd}
						disabled={loading || isDisabled || (hasVariations && !selectedVariation)}
					>
						{isLimitReached ? 'Limit Reached' : addLabel}
					</button>
				)}
			</div>
		</div>
	);
};

const CartBottomSheet = ({
	selectedItems,
	menuData,
	itemsByCategory,
	requiredCategoriesSatisfied,
	onRemoveItem,
	onSubmit,
	onClose,
	submitting,
	guestPricingMode = 'all_included',
	subtotalKobo = 0,
	hasPaidItems = false,
}) => {
	const totalItems = selectedItems.reduce((sum, i) => sum + (i.quantity || 1), 0);
	const servicePct = Number(menuData?.event?.guestPricing?.serviceChargePct) || 0;
	const serviceKobo = Math.round((subtotalKobo * servicePct) / 100);
	const totalKobo = subtotalKobo + serviceKobo;

	return (
		<>
			<div className="ev-menu-sheet-backdrop" onClick={onClose} />
			<div className="ev-menu-sheet">
				<div className="ev-menu-sheet-handle" />
				<div className="ev-menu-sheet-head">
					<h3>Your Order</h3>
					<p className="ev-menu-sheet-summary">{totalItems} item{totalItems !== 1 ? 's' : ''}</p>
				</div>

				<div className="ev-menu-sheet-block">
					{menuData?.courseTemplate?.categories.map((catConfig) => {
						const catId = catConfig.category?._id;
						const count = itemsByCategory[catId] || 0;
						const limit = catConfig.limit || 0;
						const isRequired = catConfig.required;
						const isSatisfied = !isRequired || count >= (catConfig.minRequired || 1);

						return (
							<div key={catId} className="ev-menu-rule">
								<div className="ev-menu-rule-head">
									<strong>
										{catConfig.category?.icon} {catConfig.category?.displayName}
										{isRequired && !isSatisfied ? <span> (required)</span> : ''}
									</strong>
									{limit > 0 && <span>{count}/{limit}</span>}
								</div>
								{limit > 0 && (
									<div className="ev-menu-rule-track">
										<div
											className="ev-menu-rule-fill"
											style={{ width: `${Math.min((count / limit) * 100, 100)}%` }}
										/>
									</div>
								)}
							</div>
						);
					})}
				</div>

				<div className="ev-menu-sheet-block">
					{selectedItems.map((item) => (
						<div key={`${item._id}::${item.variation?.name || ''}`} className="ev-menu-item-row">
							<div className="ev-menu-item-main">
								{item.imageUrl && <img src={item.imageUrl} alt={item.name} />}
								<div>
									<strong>{item.name}</strong>
									{item.variation?.name && (
										<p className="ev-cart-item-variation">Option: {item.variation.name}</p>
									)}
									<p>Qty: {item.quantity || 1}</p>
								</div>
							</div>
							<button
								className="ev-menu-item-remove"
								onClick={() => onRemoveItem(item._id, item.variation?.name)}
							>
								×
							</button>
						</div>
					))}
				</div>

				{hasPaidItems && (
					<div className="ev-menu-sheet-block ev-menu-totals">
						<div className="ev-menu-totals-row">
							<span>Subtotal</span>
							<span>{formatNaira(subtotalKobo)}</span>
						</div>
						{serviceKobo > 0 && (
							<div className="ev-menu-totals-row">
								<span>Service charge ({servicePct}%)</span>
								<span>{formatNaira(serviceKobo)}</span>
							</div>
						)}
						<div className="ev-menu-totals-row is-total">
							<strong>Total</strong>
							<strong>{formatNaira(totalKobo)}</strong>
						</div>
						<p className="ev-menu-totals-note">
							You'll pay securely via Paystack. Card, transfer, or USSD accepted.
						</p>
					</div>
				)}

				<div className="ev-menu-sheet-submit">
					<button
						className="ev-primary-btn"
						onClick={onSubmit}
						disabled={submitting || !requiredCategoriesSatisfied}
					>
						{submitting
							? hasPaidItems ? 'Opening checkout…' : 'Submitting...'
							: !requiredCategoriesSatisfied
								? 'Complete Required Selections'
								: hasPaidItems
									? `Pay ${formatNaira(totalKobo)} & submit`
									: 'Submit Order'}
					</button>
				</div>
			</div>
		</>
	);
};

const OrderTrackingView = ({ orderStatus, orderId, selectedItems, allergies, onNewOrder, onCancelOrder, eventName }) => {
	const statusConfig = {
		Pending:    { Icon: IconClock, title: 'Order received',  message: 'Your order is on its way to the kitchen.', step: 1 },
		Preparing:  { Icon: IconChef,  title: 'Being prepared',  message: 'The kitchen is plating your order now.',    step: 2 },
		Ready:      { Icon: IconCheck, title: 'Ready to serve',  message: 'A server is bringing it to your table.',    step: 3 },
		Delivered:  { Icon: IconPlate, title: 'Enjoy the meal',  message: 'Your order has been delivered. Bon appétit.', step: 4 },
	};

	const config = statusConfig[orderStatus] || statusConfig.Pending;
	const { Icon, step } = config;
	const steps = [
		{ label: 'Sent', icon: IconClock },
		{ label: 'Cooking', icon: IconChef },
		{ label: 'Ready', icon: IconCheck },
		{ label: 'Delivered', icon: IconPlate },
	];

	const totalItems = selectedItems.reduce((sum, i) => sum + (i.quantity || 1), 0);

	return (
		<div className="ev-order-status-view">
			<div className="ev-order-status-card">
				<div className="ev-order-status-icon" aria-hidden="true">
					<Icon className="ev-icon-svg" />
				</div>
				<h2>{config.title}</h2>
				<p>{config.message}</p>

				{/* Step rail — visualises progress through the four states. */}
				<ol className="ev-order-status-rail" aria-label="Order progress">
					{steps.map((s, i) => {
						const StepIcon = s.icon;
						const stepNumber = i + 1;
						const stateClass =
							stepNumber < step ? 'is-done' :
							stepNumber === step ? 'is-active' : 'is-pending';
						return (
							<li key={s.label} className={`ev-order-status-step ${stateClass}`}>
								<span className="ev-order-status-step-dot" aria-hidden="true">
									<StepIcon className="ev-icon-svg" />
								</span>
								<span className="ev-order-status-step-label">{s.label}</span>
							</li>
						);
					})}
				</ol>

				<div className="ev-order-status-summary">
					<div className="ev-order-status-summary-head">
						<h4>{eventName ? `${eventName} · ` : ''}Your order</h4>
						<span className="ev-order-status-summary-count">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
					</div>
					{selectedItems.map((item, idx) => (
						<div key={idx} className="ev-order-status-item">
							<span>{item.name}</span>
							<strong>×{item.quantity || 1}</strong>
						</div>
					))}
					{allergies?.length > 0 && (
						<div className="ev-order-status-item ev-order-status-allergy">
							<span>Allergies</span>
							<strong>{allergies.join(', ')}</strong>
						</div>
					)}
				</div>

				{orderStatus === 'Delivered' && orderId && (
					<MealRatingPrompt orderId={orderId} items={selectedItems} />
				)}
				{orderStatus === 'Delivered' && (
					<button className="ev-primary-btn ev-top-gap" onClick={onNewOrder}>
						Order something else
					</button>
				)}
				{orderStatus === 'Pending' && onCancelOrder && (
					<button className="ev-soft-btn ev-top-gap" onClick={onCancelOrder}>
						Cancel this order
					</button>
				)}
				<p className="ev-order-status-note">Status updates automatically · No need to refresh</p>
			</div>
		</div>
	);
};

export default CustomerOrderPage;
